import React, { useMemo, useState } from "react";

import {
  type Control,
  Controller,
  type FieldErrors,
  type UseFormSetValue,
  type UseFormWatch,
} from "react-hook-form";

import { useUser } from "@components/user-context";
import { usePrettyLogEventPage } from "@components/v2/analytics";
import {
  type Annotation,
  type NewAnnotation,
} from "@features/custom-annotations";
import { DatePicker } from "@shared/date-picker";
import { DateRangeCalendar } from "@shared/date-range-calendar";
import { FormattedDateRange } from "@shared/date-range-picker";

interface AnnotationDatePickerProps {
  currentAnnotation: Annotation | NewAnnotation;
  watch: UseFormWatch<NewAnnotation>;
  setValue: UseFormSetValue<NewAnnotation>;
  errors: FieldErrors<NewAnnotation>;
  control: Control<NewAnnotation>;
}

export const AnnotationDatePicker = ({
  currentAnnotation,
  watch,
  setValue,
  errors,
  control,
}: AnnotationDatePickerProps) => {
  const { user } = useUser();
  const annotatedDate = watch("annotatedDate");
  const endDate = watch("endDate");
  const isEdit = "id" in currentAnnotation;
  const prettyLogEventPage = usePrettyLogEventPage();
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const dateRange = useMemo(
    () => ({
      startDate: annotatedDate || null,
      endDate: endDate || annotatedDate || null,
    }),
    [annotatedDate, endDate],
  );

  if ("endDate" in currentAnnotation) {
    const onChange = (date: { startDate: string; endDate: string }) => {
      setValue("annotatedDate", date.startDate);
      setValue("endDate", date.endDate);
      setIsCalendarOpen(false);
      if (!isEdit) {
        prettyLogEventPage({
          domain: "Annotations",
          action: "onClick",
          additionalActionInfo: "date selector on New Note flyout",
          data: {
            annotatedDate: date,
          },
        });
      }
    };

    return (
      <div className="nb-input-container text-sm relative">
        <label className="label">DATE RANGE</label>
        <div
          className="nb-input"
          onClick={() =>
            setIsCalendarOpen((prevIsCalendarOpen) => !prevIsCalendarOpen)
          }
        >
          {annotatedDate && endDate ? (
            <span>
              <FormattedDateRange
                dateRange={{ startDate: annotatedDate, endDate }}
              />
            </span>
          ) : (
            <span className="text-disabled">Select...</span>
          )}
        </div>
        {isCalendarOpen && (
          <span className="calendar z-10 absolute right-0">
            <DateRangeCalendar
              className="bg-white rounded-lg elevation-1"
              value={dateRange}
              onApply={onChange}
              onCancel={() => setIsCalendarOpen(false)}
            />
          </span>
        )}
      </div>
    );
  }

  return (
    <Controller
      control={control}
      name="annotatedDate"
      rules={{ required: "Date is required" }}
      render={({ field }) => {
        const onChange = (date: string | null) => {
          field.onChange(date);
          if (!isEdit) {
            prettyLogEventPage({
              domain: "Annotations",
              action: "onClick",
              additionalActionInfo: "date selector on New Note flyout",
              data: {
                annotatedDate: date,
              },
            });
          }
        };
        return (
          <DatePicker
            {...field}
            onChange={onChange}
            name="annotatedDate"
            timezone={user.timezone}
            isDisabled={isEdit}
            errors={errors}
          />
        );
      }}
    />
  );
};
